import { Reducer, Action } from 'redux';
import { withDefaultFiltersReducer } from 'app/area/shared/default-filters/reducers';
import { withSavedReportsReducer } from 'app/area/shared/saved-reports/reducers';
import {
  clearValidationForAllParameters, clearValidationForKeys, clearValidationForSingleParameter
} from 'app/area/shared/report/form/validation';
import {
  ACTION, ReceiveLanguageOptions,
  RequestDashboard, ReceiveDashboard, FailDashboard,
  RequestDeleteDashboard, ReceiveDeleteDashboard, FailDeleteDashboard,
  RequestSaveDashboard, ReceiveSaveDashboard, FailSaveDashboard,
  UpdateItem, ChangeLanguage, CopyLanguageToAll,
  ClearForm, UpdateValidation
} from './action-types';
import { DashboardComponentForm, languageValidationParameter } from './constants';
import { DashboardAdminFormState, DashboardItem } from './types-store';
import { CompanyReportItemValidation } from 'app/area/company-reports-administration/form/types-store';

export const INITIAL_STATE: DashboardAdminFormState = {
  dashboardItem: undefined,
  originalDashboardItem: undefined,
  filters: undefined,
  originalFilters: undefined,
  typeId: undefined,
  newDashboardId: undefined,
  fetching: false,
  deleting: false,
  saving: false,
  languageOptions: [],
  validation: {},
  savedReports: undefined,
  originalSavedReports: undefined,
  displayDisabled: false,
  currentSavedReportId: undefined,
  currentSavedReport: undefined,
  currentSavedReportOnlyIdMode: false,
  currentValidation: undefined,
  fetchingCurrentEvaluation: false,
  fetchingAddSavedReports: false,
  savedReportsValidation: {},
  filterMetadata: {},
  fetchingFilterMetadata: [],
  filterValidation: {}
};

const reducers: { [indexer: string]: Reducer<DashboardAdminFormState, Action> } = {

  [ACTION.RECEIVE_LANGUAGE_OPTIONS]: (state: DashboardAdminFormState,
    action: ReceiveLanguageOptions): DashboardAdminFormState => {

    return {
      ...state,
      languageOptions: action.options
    };
  },

  [ACTION.REQUEST_DASHBOARD]: (state: DashboardAdminFormState,
    action: RequestDashboard): DashboardAdminFormState => {

    return {
      ...state,
      dashboardItem: undefined,
      originalDashboardItem: undefined,
      savedReports: undefined,
      originalSavedReports: undefined,
      filters: undefined,
      originalFilters: undefined,
      typeId: action.id,
      newDashboardId: undefined,
      fetching: true,
      deleting: false,
      saving: false,
      validation: {},
      currentSavedReport: undefined,
      savedReportsValidation: {}
    };
  },

  [ACTION.RECEIVE_DASHBOARD]: (state: DashboardAdminFormState,
    action: ReceiveDashboard): DashboardAdminFormState => {

    const filterMetadata = action.filterMetadata
      .reduce((prev, filter) => ({
        ...prev,
        [filter.domain.value]: filter
      }), state.filterMetadata);

    return state.typeId === action.id
      ? {
        ...state,
        dashboardItem: action.item,
        originalDashboardItem: action.item,
        savedReports: action.savedReports,
        originalSavedReports: action.savedReports,
        filters: action.filters,
        originalFilters: action.filters,
        filterMetadata,
        fetching: false
      }
      : state;
  },

  [ACTION.FAIL_DASHBOARD]: (state: DashboardAdminFormState,
    action: FailDashboard): DashboardAdminFormState => {

    return state.typeId === action.id
      ? {
        ...state,
        fetching: false
      }
      : state;
  },

  [ACTION.REQUEST_DELETE_DASHBOARD]: (state: DashboardAdminFormState,
    action: RequestDeleteDashboard): DashboardAdminFormState => {

    return state.typeId === action.id
      ? {
        ...state,
        deleting: true
      }
      : state;
  },

  [ACTION.RECEIVE_DELETE_DASHBOARD]: (state: DashboardAdminFormState,
    action: ReceiveDeleteDashboard): DashboardAdminFormState => {

    return state.typeId === action.id
      ? {
        ...state,
        deleting: false
      }
      : state;
  },

  [ACTION.FAIL_DELETE_DASHBOARD]: (state: DashboardAdminFormState,
    action: FailDeleteDashboard): DashboardAdminFormState => {

    return state.typeId === action.id
      ? {
        ...state,
        deleting: false
      }
      : state;
  },

  [ACTION.REQUEST_SAVE_DASHBOARD]: (state: DashboardAdminFormState,
    action: RequestSaveDashboard): DashboardAdminFormState => {

    return {
      ...state,
      typeId: action.id,
      saving: true
    };
  },

  [ACTION.RECEIVE_SAVE_DASHBOARD]: (state: DashboardAdminFormState,
    action: ReceiveSaveDashboard): DashboardAdminFormState => {

    return state.typeId === action.id
      ? {
        ...state,
        newDashboardId: action.newDashboardId,
        originalDashboardItem: state.dashboardItem,
        originalSavedReports: state.savedReports,
        saving: false
      }
      : state;
  },

  [ACTION.FAIL_SAVE_DASHBOARD]: (state: DashboardAdminFormState,
    action: FailSaveDashboard): DashboardAdminFormState => {

    return state.typeId === action.id
      ? {
        ...state,
        saving: false
      }
      : state;
  },

  [ACTION.UPDATE_ITEM]: (state: DashboardAdminFormState,
    action: UpdateItem): DashboardAdminFormState => {

    const dashboardItem = {
      ...(state.dashboardItem || {}),
      ...action.item
    } as DashboardItem;
    const validation = clearValidationForKeys(state.validation, Object.keys(action.item) as (keyof CompanyReportItemValidation)[]);

    return {
      ...state,
      dashboardItem,
      validation
    };
  },

  [ACTION.CHANGE_LANGUAGE]: (state: DashboardAdminFormState,
    action: ChangeLanguage): DashboardAdminFormState => {

    const dashboardItem = state.dashboardItem || {} as DashboardItem;
    const language = dashboardItem.language || [];

    return {
      ...state,
      dashboardItem: {
        ...dashboardItem,
        language: language.some(languageItem => languageItem.value === action.languageId)
          ? language.map(
            languageItem => languageItem.value === action.languageId
              ? {
                ...languageItem,
                description: action.description
              }
              : languageItem
          )
          : [
            ...language,
            {
              value: action.languageId,
              description: action.description
            }
          ]
      },
      validation: clearValidationForSingleParameter(state.validation, 'language', languageValidationParameter, action.languageId)
    };
  },

  [ACTION.COPY_LANGUAGE_TO_ALL]: (state: DashboardAdminFormState,
    action: CopyLanguageToAll): DashboardAdminFormState => {

    const dashboardItem = state.dashboardItem || {} as DashboardItem;
    const language = dashboardItem.language || [];
    const targetLanguage = language.find(languageItem => languageItem.value === action.languageId);

    if (targetLanguage === undefined) {
      return state;
    }

    const updatedLanguage = language.map(
      languageItem => ({
        ...languageItem,
        description: targetLanguage.description
      })
    );
    const newLanguage = (state.languageOptions || [])
      .map(option => option.value)
      .filter(languageId => !language.some(languageItem => languageItem.value === languageId))
      .map(languageId => ({
        value: languageId,
        description: targetLanguage.description
      }));

    return {
      ...state,
      dashboardItem: {
        ...dashboardItem,
        language: [
          ...updatedLanguage,
          ...newLanguage
        ]
      },
      validation: clearValidationForAllParameters(state.validation, 'language', languageValidationParameter)
    };
  },

  [ACTION.CLEAR_FORM]: (state: DashboardAdminFormState,
    action: ClearForm): DashboardAdminFormState => {

    return {
      ...state,
      ...INITIAL_STATE,
      languageOptions: state.languageOptions,
      filterMetadata: state.filterMetadata,
      fetchingFilterMetadata: state.fetchingFilterMetadata
    };
  },

  [ACTION.UPDATE_VALIDATION]: (state: DashboardAdminFormState,
    action: UpdateValidation): DashboardAdminFormState => {

    return {
      ...state,
      validation: action.validation
    };
  }
};

const pureReducer: Reducer<DashboardAdminFormState> = (
  state: DashboardAdminFormState = INITIAL_STATE, action: Action) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};

export const reducer = withDefaultFiltersReducer(DashboardComponentForm,
  withSavedReportsReducer(DashboardComponentForm, pureReducer));
